import { CharacterSchema, type Character } from '@/schemas/character';
import { migrateCharacter } from './characterMigration';

/**
 * Snapshot del personaggio pre-azione, persistito in sessionStorage per
 * supportare il pattern `<UndoToast>` cross-page (es. `LevelUpFlowPage`
 * scrive lo snapshot prima di navigare a `/character`; `CharacterViewPage`
 * lo legge al mount e mostra il toast).
 *
 * Pattern stateless: lo snapshot vive in sessionStorage solo per la finestra
 * di countdown (8s default + 2s buffer). `readUndoSnapshot` valida con Zod
 * e scarta se il TTL è scaduto.
 */

const UNDO_KEY = 'dwr.undo-snapshot.v1';
const UNDO_TTL_MS = 10_000; // 8s countdown + 2s buffer per nav

export interface UndoSnapshot {
  character: Character;
  message: string;
  triggeredAt: number;
}

interface SerializedUndoSnapshot {
  character: unknown;
  message: string;
  triggeredAt: number;
}

function getStorage(): Storage | null {
  if (typeof window === 'undefined') return null;
  return window.sessionStorage;
}

export function writeUndoSnapshot(snapshot: { character: Character; message: string }): void {
  const storage = getStorage();
  if (!storage) return;
  const validated = CharacterSchema.parse(snapshot.character);
  const payload: SerializedUndoSnapshot = {
    character: validated,
    message: snapshot.message,
    triggeredAt: Date.now(),
  };
  storage.setItem(UNDO_KEY, JSON.stringify(payload));
}

/**
 * Legge lo snapshot dal sessionStorage e lo elimina subito (read-once).
 * Ritorna `null` se assente, malformato o scaduto. La validazione con
 * `CharacterSchema` protegge da snapshot corrotti — l'errore non viene
 * propagato perché lo snapshot è opzionale (l'undo è polish, non critico).
 */
export function readUndoSnapshot(): UndoSnapshot | null {
  const storage = getStorage();
  if (!storage) return null;
  const raw = storage.getItem(UNDO_KEY);
  if (raw === null) return null;
  storage.removeItem(UNDO_KEY);
  try {
    const parsed = JSON.parse(raw) as SerializedUndoSnapshot;
    if (typeof parsed?.triggeredAt !== 'number' || typeof parsed?.message !== 'string') {
      return null;
    }
    if (Date.now() - parsed.triggeredAt > UNDO_TTL_MS) return null;
    const character = migrateCharacter(parsed.character);
    return { character, message: parsed.message, triggeredAt: parsed.triggeredAt };
  } catch {
    return null;
  }
}

export function clearUndoSnapshot(): void {
  const storage = getStorage();
  if (!storage) return;
  storage.removeItem(UNDO_KEY);
}
